import React, { useContext, useEffect, useState } from 'react'
import { CountryDto } from '@/api/Identity/Types/Country'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import {
  NPaymentProviderDto,
  NPaymentProviderMethodConfiguration,
} from '@/api/NPayments/Types/NPaymentProviderDto'
import {
  CurrencyMultiSelectFilterCell,
  WalletTypeMultiSelectFilterCell,
} from '@/components/Filter/common.filter'
import {
  FormBaseButtonActionWrapper,
  FormBaseNavigationHeader,
} from '@/components/Form/Form.styles'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { normalizeFilter } from '@/features/DataGrid'
import { useAppDispatch, useAppSelector } from '@/store'
import { pageableSettings } from '@/store/grid/grid.slice'
import { fetchCountriesAction } from '@/store/identity/identity.actions'
import { getWalletCurrenciesAction, getWalletTypesAction } from '@/store/wallet/wallet-actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { WalletsApiClient } from '@/utils/api/walletsApiClient'
import { AuthSessionContext } from '@/utils/AuthContext'
import { isEmpty } from '@/utils/is-empty'
import { hasPermission } from '@/utils/permissionsValidator'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridFilterChangeEvent,
  GridHeaderSelectionChangeEvent as GridHeaderSelectionChangeEventType,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import MoreButton from 'components/MoreButton/MoreButton'
import { Form, Formik, FormikProps } from 'formik'
import { ChevronDown } from 'lucide-react'
import { v4 as uuid } from 'uuid'

import AddButton from '../../../components/AddButton/AddButton'
import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { CountryFilterCell } from '../Countries/CountriesFilters'
import PaymentProviderMethodConfigurationForm from './PaymentProviderMethodConfigurationForm'

interface SubscriptionsDetailsMainProps {
  isEdit: boolean
  data: NPaymentProviderMethodConfiguration[]
  methodAddEditModalOpen(data?: NPaymentProviderMethodConfiguration): void
  onMethodDeleteModalOpen(data: NPaymentProviderMethodConfiguration): void
  onSelectionChange(selectedItems: NPaymentProviderMethodConfiguration[]): void
}

interface FormValues {
  methodConfigurations: NPaymentProviderDto['methodConfigurations']
}

export const PaymentProviderCountriesTable: React.FC<SubscriptionsDetailsMainProps> = (props) => {
  const { data, methodAddEditModalOpen, onMethodDeleteModalOpen, isEdit, onSelectionChange } = props
  const [methodConfigurations, setMethodConfigurations] =
    useState<NPaymentProviderDto['methodConfigurations']>(data)
  const [page, setPage] = useState<{ skip: number; take: number }>({ skip: 0, take: 10 })
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [],
  })

  useEffect(() => {
    setMethodConfigurations(data)
  }, [data])

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({
      skip: event.page.skip,
      take: event.page.take,
    })
  }

  const handleGridSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!isEdit) {
      return
    }
    const modifiedElements = methodConfigurations.map((item) => {
      if (item.id === event.dataItem.id) {
        return { ...item, selected: !event.dataItem.selected }
      }
      return item
    })
    setMethodConfigurations(modifiedElements)
    onSelectionChange(modifiedElements)
  }

  const handleHeaderSelectionChange = (
    event: GridHeaderSelectionChangeEventType<HTMLInputElement>
  ) => {
    if (!isEdit) {
      return
    }
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    const filteredData = filterBy(methodConfigurations || [], filter)
    const filteredIds = new Set(filteredData.map((item) => item.id))
    const modifiedElements = methodConfigurations.map((item) => ({
      ...item,
      selected: filteredIds.has(item.id) ? isChecked : item.selected,
    }))
    setMethodConfigurations(modifiedElements)
    onSelectionChange(modifiedElements)
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPage({ ...page, skip: 0 })
  }

  const gridData = filterBy(methodConfigurations || [], filter)

  return (
    <Grid
      selectedField='selected'
      sortable={{
        allowUnsort: true,
        mode: 'multiple',
      }}
      data={gridData.slice(page.skip, page.skip + page.take)}
      skip={page.skip}
      take={page.take}
      total={gridData.length}
      pageSize={page.take}
      pageable={pageableSettings}
      onPageChange={handlePageChange}
      filterable={true}
      filter={filter}
      onFilterChange={handleFilterChange}
      onSelectionChange={handleGridSelectionChange}
      onHeaderSelectionChange={handleHeaderSelectionChange}
      style={{ zIndex: 0 }}
    >
      <Column
        field='selected'
        width='50px'
        title=''
        filterCell={() => null}
        headerSelectionValue={
          methodConfigurations.findIndex((dataItem) => !dataItem.selected) === -1
        }
      />
      <Column
        field='walletType.name'
        title='Wallet Type'
        filterCell={WalletTypeMultiSelectFilterCell}
      />
      <Column
        field='walletCurrency.id'
        title='Currency'
        filterCell={CurrencyMultiSelectFilterCell}
      />
      <Column field='country.name' title='Country' filterCell={CountryFilterCell} />
      <Column
        width={40}
        title=''
        filterCell={() => null}
        cell={(props) => (
          <td>
            <MoreButton
              disabled={!isEdit}
              items={[
                {
                  name: 'Edit',
                  'data-test': 'edit',
                  disabled: !isEdit,
                  onClick: () => methodAddEditModalOpen(props.dataItem),
                },
                {
                  name: 'Delete',
                  'data-test': 'delete',
                  disabled: !isEdit,
                  onClick: () => onMethodDeleteModalOpen(props.dataItem),
                },
              ]}
              data-test='more-button'
            />
          </td>
        )}
      />
    </Grid>
  )
}

interface OuterProps {
  data: NPaymentProviderDto
  handleSubmit(config: NPaymentProviderDto): void
  isEdit: boolean
  setEdit: React.Dispatch<React.SetStateAction<boolean>>
}

export const PaymentProviderCountriesDetailsPage: React.FC<OuterProps> = (props) => {
  const { data, handleSubmit, isEdit, setEdit } = props
  const [auth] = useContext(AuthSessionContext)
  const [selectedItems, setSelectedItems] = useState<NPaymentProviderMethodConfiguration[]>([])
  const [confirmationModal, setConfirmationModal] = useState(false)
  const permissions = auth?.permissions || []
  const [isFormModalOpen, setFormModalOpen] = useState<{
    data?: NPaymentProviderMethodConfiguration
    visible: boolean
  }>({
    visible: false,
    data: undefined,
  })

  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const walletTypes = useAppSelector((state) => state.wallet.walletType)
  const countries: CountryDto[] = useAppSelector((state) => state.identity.countries)
  const walletCurrencies = useAppSelector((state) => state.wallet.currencies)

  useEffect(() => {
    if (isEmpty(walletTypes)) {
      dispatch(getWalletTypesAction(walletsApiClient))
    }
    if (isEmpty(countries)) {
      dispatch(fetchCountriesAction(identityApiClient))
    }
    if (isEmpty(walletCurrencies)) {
      dispatch(getWalletCurrenciesAction(walletsApiClient))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues: FormValues = {
    methodConfigurations: data.methodConfigurations,
  }
  const selectedItemsCount = selectedItems.filter((item) => item.selected).length

  return (
    <React.Fragment>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          paymentProviderId={data.id}
        />
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, actions) => {
          handleSubmit({ ...data, methodConfigurations: values.methodConfigurations })
          actions.setSubmitting(false)
        }}
      >
        {(formikProps: FormikProps<FormValues>) => {
          const handleSelectionChange = (
            newSelectedItems: NPaymentProviderMethodConfiguration[]
          ) => {
            setSelectedItems(newSelectedItems)
          }

          const handleConfirmationModalCancel = () => {
            setConfirmationModal(false)
          }

          const handleDeleteSelected = () => {
            handleSubmit({
              ...data,
              methodConfigurations: selectedItems.filter((c) => !c.selected),
            })
          }

          const handleMethodConfigurationSubmit = (
            values: NPaymentProviderMethodConfiguration & { countries: CountryDto[] }
          ) => {
            const currentMethodConfigs = formikProps.values.methodConfigurations
            let nPaymentProviderMethodConfigurations: NPaymentProviderMethodConfiguration[]

            if (values.id) {
              nPaymentProviderMethodConfigurations = currentMethodConfigs.map((md) =>
                md.id === values.id ? values : md
              )
            } else {
              const newConfigurations = values.countries
                .map((country) => {
                  const hasTheSameOption = currentMethodConfigs.find(
                    (c) =>
                      c.walletCurrency.id === values.walletCurrency.id &&
                      c.walletType.id === values.walletType.id &&
                      c.country?.id === country.id
                  )
                  if (!hasTheSameOption) {
                    return {
                      id: uuid(),
                      walletCurrency: values.walletCurrency,
                      walletType: values.walletType,
                      country: { id: country.id, name: country.name },
                    }
                  }
                  return null
                })
                .filter(Boolean) as NPaymentProviderMethodConfiguration[]
              nPaymentProviderMethodConfigurations = [...currentMethodConfigs, ...newConfigurations]
            }

            formikProps.setFieldValue('methodConfigurations', nPaymentProviderMethodConfigurations)
            setFormModalOpen({ data: undefined, visible: false })
          }

          return (
            <React.Fragment>
              {confirmationModal && (
                <ConfirmationModal
                  contentText={`Are you sure you want to delete selected rows? (${selectedItems.filter((x) => x.selected).length} of ${formikProps.values.methodConfigurations.length} records)`}
                  yesButtonText='Proceed'
                  onYes={handleDeleteSelected}
                  onCancel={handleConfirmationModalCancel}
                  visible={confirmationModal}
                />
              )}
              <Form>
                <FormBaseNavigationHeader style={{ justifyContent: 'space-between' }}>
                  <AddButton
                    disabled={!isEdit}
                    text='Add new country'
                    onClick={() => setFormModalOpen({ data: undefined, visible: true })}
                    data-test='add-new-country-button'
                  />
                  <FormBaseButtonActionWrapper style={{ margin: '1rem' }}>
                    <ButtonGroup>
                      {!isEdit ? (
                        <Button
                          type='button'
                          disabled={
                            !hasPermission(permissions, PermissionsEnum.CanEditPaymentProviders)
                          }
                          onClick={() => setEdit(true)}
                        >
                          Edit
                        </Button>
                      ) : (
                        <>
                          <Button
                            type='button'
                            variant='destructive'
                            disabled={selectedItemsCount === 0} // Changed from getSelectedItems.length < 1
                            onClick={() => setConfirmationModal(true)}
                          >
                            Delete
                          </Button>

                          <Button
                            type='button'
                            variant='outline'
                            onClick={() => {
                              setEdit(false)
                              formikProps.resetForm()
                            }}
                          >
                            Cancel
                          </Button>

                          <Button type='submit'>Save</Button>
                        </>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          render={
                            <Button
                              type='button'
                              variant='outline'
                              size='icon'
                              aria-label='More options'
                            />
                          }
                        >
                          <ChevronDown />
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem
                            onSelect={() => setAuditLogVisible(true)}
                            data-test='audit-log-button'
                          >
                            Audit Log
                          </DropdownMenuItem>

                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger data-test='status-menu-item'>
                              Status
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuItem
                                disabled={data.enabled}
                                onSelect={() => {
                                  handleSubmit({
                                    ...data,
                                    enabled: true,
                                  })
                                }}
                                data-test='enable-status'
                              >
                                Enable
                              </DropdownMenuItem>

                              <DropdownMenuItem
                                disabled={!data.enabled}
                                onSelect={() => {
                                  handleSubmit({
                                    ...data,
                                    enabled: false,
                                  })
                                }}
                                data-test='disable-status'
                              >
                                Disable
                              </DropdownMenuItem>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </ButtonGroup>
                  </FormBaseButtonActionWrapper>
                </FormBaseNavigationHeader>

                {isFormModalOpen.visible && (
                  <PaymentProviderMethodConfigurationForm
                    open={isFormModalOpen.visible}
                    walletType={walletTypes}
                    countries={countries}
                    existingConfigurations={formikProps.values.methodConfigurations.flatMap((c) =>
                      c.country
                        ? [
                            {
                              id: c.id,
                              walletTypeId: c.walletType.id,
                              currencyId: c.walletCurrency.id,
                              countryId: c.country.id,
                            },
                          ]
                        : []
                    )}
                    walletCurrencies={walletCurrencies}
                    v={
                      isFormModalOpen.data
                        ? {
                            id: isFormModalOpen.data.id,
                            walletTypeId: isFormModalOpen.data.walletType.id,
                            countryId: isFormModalOpen.data.country?.id,
                            currencyId: isFormModalOpen.data.walletCurrency.id,
                          }
                        : {
                            id: '',
                            walletTypeId: undefined,
                            currencyId: '',
                            countryId: '',
                          }
                    }
                    onSubmit={(values) => handleMethodConfigurationSubmit(values)}
                    onClose={() =>
                      setFormModalOpen({
                        data: undefined,
                        visible: false,
                      })
                    }
                  />
                )}

                <PaymentProviderCountriesTable
                  isEdit={isEdit}
                  data={formikProps.values.methodConfigurations}
                  onMethodDeleteModalOpen={(d) => {
                    const filtered = formikProps.values.methodConfigurations.filter(
                      (item) => item.id !== d.id
                    )
                    formikProps.setFieldValue('methodConfigurations', filtered)
                  }}
                  methodAddEditModalOpen={(d) => setFormModalOpen({ data: d, visible: true })}
                  onSelectionChange={handleSelectionChange}
                />
              </Form>
            </React.Fragment>
          )
        }}
      </Formik>
    </React.Fragment>
  )
}
